import { useEffect, useRef } from 'react';
import type { FormInstance } from 'antd';
import dayjs from 'dayjs';
import type { Planting } from '../../../../../models/Planting';
import type { LockedLocationIds, PlantingFormModalValues, PlantingFormModalValuesLocation } from '../types';
import { normalizeId, toFormDate } from '../utils';

interface UseFormInitializationOptions {
  open: boolean;
  form: FormInstance<PlantingFormModalValues>;
  initialPlanting?: Planting | null;
  defaultLocations?: Planting['locations'];
  defaultCropId?: string | number | null;
  lockedLocation: LockedLocationIds | null;
  /** Callbacks triggered after form is initialized */
  onInitialized: (params: {
    normalizedLocations: PlantingFormModalValuesLocation[];
    cropId?: string;
    bedId?: string;
  }) => void;
  /** Reset ephemeral state when modal closes */
  onClose: () => void;
}

/**
 * Handles form field initialization when modal opens,
 * including data prefetching signals.
 */
export const useFormInitialization = ({
  open,
  form,
  initialPlanting,
  defaultLocations,
  defaultCropId,
  lockedLocation,
  onInitialized,
  onClose,
}: UseFormInitializationOptions) => {
  const initialLocationsRef = useRef<PlantingFormModalValuesLocation[]>([]);

  useEffect(() => {
    if (!open) {
      onClose();
      return;
    }

    const sourceLocations = initialPlanting?.locations?.length
      ? initialPlanting.locations
      : defaultLocations && defaultLocations.length
        ? defaultLocations
        : undefined;

    const locationsForForm = sourceLocations
      ? sourceLocations.map((location) => ({
          id: location.id,
          fieldId: location.field?.id ?? location.plot?.field_id ?? undefined,
          plotId: location.plot?.id ?? undefined,
          bedId: location.bed?.id ?? location.bedId ?? undefined,
          fromDate: toFormDate(location.fromDate) ?? dayjs(),
          toDate: toFormDate(location.toDate) ?? null,
        }))
      : [{ fromDate: dayjs(), toDate: null }];

    const normalizedLocations = locationsForForm.map((location, index) => {
      if (!lockedLocation || index > 0) {
        return {
          ...location,
          fieldId: location.fieldId !== undefined ? normalizeId(location.fieldId) : location.fieldId,
          plotId: location.plotId !== undefined ? normalizeId(location.plotId) : location.plotId,
          bedId: location.bedId !== undefined ? normalizeId(location.bedId) : location.bedId,
        };
      }
      return {
        ...location,
        fieldId: lockedLocation.fieldId,
        plotId: lockedLocation.plotId,
        bedId: lockedLocation.bedId,
      };
    });

    form.setFieldsValue({
      cropId: initialPlanting?.crop?.id ?? defaultCropId ?? undefined,
      quantity: initialPlanting?.quantity ?? 1,
      lifecycleStatus: initialPlanting?.lifecycleStatus,
      statusReason: initialPlanting?.statusReason ?? null,
      originDate: toFormDate(initialPlanting?.originDate),
      originType:
        initialPlanting?.originType && initialPlanting.originType !== 'unknown'
          ? initialPlanting.originType
          : undefined,
      locations: normalizedLocations,
    });

    initialLocationsRef.current = normalizedLocations;

    const initCropId = initialPlanting?.crop?.id ?? defaultCropId;
    const initBedId = lockedLocation?.bedId ?? normalizedLocations[0]?.bedId;

    onInitialized({
      normalizedLocations,
      cropId: initCropId ? String(initCropId) : undefined,
      bedId: initBedId ? String(initBedId) : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialPlanting, defaultLocations, defaultCropId, lockedLocation]);

  return { initialLocationsRef };
};
